import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { getFetchError } from '@/reducers/fetch'
import { TypographyStyles } from '@/styles'

import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { faPercent, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import {
  Alert,
  Button,
  Card,
  CardColors,
  Form,
  Link,
  Separator,
  Text,
} from '@/ui'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'

import {
  isValidUSState,
  stateAbbToState,
  STATES_LONG,
} from '@/constants/locationConstants'

import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import { FilingStatusSubForm } from './FilingStatusSubForm'

import {
  FormikLabelError,
  getFieldName,
  makeNumberSchema,
  makeReqBoolSchema,
  makeReqStringSchema,
} from '@/base'
import {
  FILING_STATUSES,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'

export const AboutYouForm = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToNextStep } = useTaxProfileSteps(TAX_PROFILE_STEP.aboutYou)

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      filingStatusSame: formFlowAnswers?.filingStatusSame,
      filingStatus: fp?.filingStatus,
      stateSame: formFlowAnswers?.stateSame,
      relocatedPreviousState: fp?.relocatedPreviousState,
      relocatedPreviousStateAt:
        fp?.relocatedPreviousStateAt &&
        moment.utc(fp?.relocatedPreviousStateAt).format(DATE_FORMATS.INPUT),
      homeState: isValidUSState(fp?.homeState) ? fp?.homeState : undefined,
      registrationStateSame: formFlowAnswers?.registrationStateSame,
      scorpRegistrationState: fp?.scorpRegistrationState,
      percentageIncomePerState: fp?.percentageIncomePerState || [
        { state: undefined, value: undefined },
      ],
    },
    onSubmit: async ({
      filingStatusSame,
      filingStatus,
      stateSame,
      relocatedPreviousState,
      relocatedPreviousStateAt,
      homeState,
      registrationStateSame,
      scorpRegistrationState,
      percentageIncomePerState,
    }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          ...((filingStatusSame === false || !fp?.filingStatus) && {
            filingStatus,
          }),
          ...(stateSame
            ? {
                relocatedPreviousState: null,
                relocatedPreviousStateAt: null,
              }
            : {
                relocatedPreviousState,
                relocatedPreviousStateAt,
                homeState,
              }),
          ...((registrationStateSame === false ||
            !fp?.scorpRegistrationState) && {
            scorpRegistrationState,
          }),
          // Clear spouse fields if not filing jointly or qualifying widow
          ...(filingStatus &&
            ![
              FILING_STATUSES.married_filing_jointly,
              FILING_STATUSES.qualifying_widow,
            ].includes(filingStatus) && {
              spouseWIncomeInCents: null,
              spouseFederalWithholdingInCents: null,
              spouseStateWithholdingInCents: null,
              spouseIndividualIncomeInCents: null,
              estimatedAnnualSpouseW2IncomeInCents: null,
            }),
          percentageIncomePerState,
        })
      )

      if (res) {
        const newAnswers = {
          ...formFlowAnswers,
          // always default to true because they either selected yes on the previous page,
          // or they selected no and updated the value, which should then now be true
          filingStatusSame: true,
          stateSame: true,
          registrationStateSame: true,
        }
        setFormFlowAnswers(newAnswers)
        goToNextStep(res)
      }
    },
  })

  const {
    values,
    setFieldValue,
    validateForm,
    submitForm,
    isValid,
    isSubmitting,
  } = formik

  const isScorp = fp?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s

  // If filingStatusSame toggled to true reset the filingStatus change
  useEffect(() => {
    if (values.filingStatusSame) {
      setFieldValue(
        getFieldName<typeof values>('filingStatus'),
        fp?.filingStatus
      )
    }
  }, [fp?.filingStatus, setFieldValue, values.filingStatusSame])

  // If stateSame toggled to true reset the stateSame change
  useEffect(() => {
    if (values.stateSame) {
      setFieldValue(getFieldName<typeof values>('homeState'), fp?.homeState)
    } else if (values.stateSame === false) {
      // If values.stateSame is toggled to false, set previous state as the current state and wipe the current state
      setFieldValue(
        getFieldName<typeof values>('relocatedPreviousState'),
        fp?.homeState
      )
      setFieldValue(getFieldName<typeof values>('homeState'), '')
    }
  }, [fp?.homeState, setFieldValue, values.stateSame])

  // If registrationStateSame toggled to true reset the registrationStateSame change
  useEffect(() => {
    if (values.registrationStateSame) {
      setFieldValue(
        getFieldName<typeof values>('scorpRegistrationState'),
        fp?.scorpRegistrationState
      )
    }
  }, [fp?.scorpRegistrationState, setFieldValue, values.registrationStateSame])

  const handleRemove = useCallback(
    (idx: number) => {
      const newStates = [...values.percentageIncomePerState]
      newStates.splice(idx, 1)
      setFieldValue('percentageIncomePerState', newStates).then(() => {
        // This should be automatic but doesn't seem to work for some reason
        validateForm()
      })
    },
    [values.percentageIncomePerState, setFieldValue, validateForm]
  )

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-4">
        <Text className="text-center" variant={TypographyStyles.DISPLAY_SM}>
          Please confirm your information
        </Text>

        <Text variant={TypographyStyles.BODY_LG} className="text-center">
          If anything has changed below, please update it now.
        </Text>

        {error && <Alert variant="error" description={error.message} />}

        <Card color={CardColors.LEVEL_0} className="p-6 space-y-8">
          <FilingStatusSubForm fp={fp} />
          <br />
          <Separator />
          <br />

          {fp?.homeState && isValidUSState(fp.homeState) ? (
            <div className="space-y-4">
              <FormikLabelError
                name={getFieldName<typeof values>('stateSame')}
                required
                label={
                  <>
                    Are you still living in{' '}
                    <b>{stateAbbToState(fp?.homeState)}</b>?
                  </>
                }
                description="If you moved to another state since last quarter, select No."
                schema={makeReqBoolSchema()}
              />

              <div className="flex flex-wrap gap-4">
                <Form.BooleanRadioGroup
                  name={getFieldName<typeof values>('stateSame')}
                  onValueChange={(val) => setFieldValue('stateSame', val)}
                  className="w-[120px]"
                />
              </div>
            </div>
          ) : (
            <Form.Select
              name={getFieldName<typeof values>('homeState')}
              label="What is your state of residence?"
              placeholder="Choose a state"
              required
              options={STATES_LONG.filter((s) => isValidUSState(s.value))}
              schema={makeReqStringSchema()}
            />
          )}

          {values.stateSame === false && (
            <>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="sm:w-2/3 w-full">
                  <Form.Select
                    name={getFieldName<typeof values>('relocatedPreviousState')}
                    label="Which state did you move from?"
                    placeholder="Choose a state"
                    required
                    options={STATES_LONG}
                    schema={makeReqStringSchema()}
                  />
                </div>
                <div className="sm:w-1/3 w-full">
                  <Form.DateInput
                    name={getFieldName<typeof values>(
                      'relocatedPreviousStateAt'
                    )}
                    label="When did you move?"
                    placeholder="Choose a date"
                    required
                    fullWidth
                  />
                </div>
              </div>

              <Form.Select
                name={getFieldName<typeof values>('homeState')}
                label="Which state did you move to?"
                placeholder="Choose a state"
                required
                options={STATES_LONG}
                schema={makeReqStringSchema()}
              />
            </>
          )}
          <br />

          <Separator />
          <br />

          {isScorp && (
            <>
              {fp?.scorpRegistrationState && (
                <div className="space-y-4">
                  <FormikLabelError
                    name={getFieldName<typeof values>('registrationStateSame')}
                    required
                    label={`Is your business still registered in ${stateAbbToState(
                      fp.scorpRegistrationState
                    )}?`}
                    description="If this has changed, select No."
                    schema={makeReqBoolSchema()}
                  />
                  <div className="flex flex-wrap gap-4">
                    <Form.BooleanRadioGroup
                      name={getFieldName<typeof values>(
                        'registrationStateSame'
                      )}
                      onValueChange={(val) => setFieldValue('stateSame', val)}
                      className="w-[120px]"
                    />
                  </div>
                </div>
              )}

              {(!fp?.scorpRegistrationState ||
                values.registrationStateSame === false) && (
                <Form.Select
                  name={getFieldName<typeof values>('scorpRegistrationState')}
                  label="What state is your business registered in?"
                  placeholder="Choose a state"
                  required
                  options={STATES_LONG}
                  schema={makeReqStringSchema()}
                />
              )}
              <br />

              <Separator />
              <br />
            </>
          )}

          <br />

          <FormikLabelError
            name={getFieldName<typeof values>('percentageIncomePerState')}
            label="What % of income do you make in each state?"
            description={
              <>
                Please share the % of income made in each state.{' '}
                <Link
                  newPage
                  href="https://support.joinheard.com/hc/en-us/articles/4763301299991"
                >
                  How do I calculate this?
                </Link>
                <br />
                Listing all states doesn’t necessarily impact your quarterly tax
                payment.
              </>
            }
            schema={yup
              .array()
              .test(
                'percentageIncomePerState',
                'Percentages must add up to 100%',
                (percentages) =>
                  percentages
                    ?.map((val) => (val.value ? Number(val.value) : 0))
                    .reduce((prev, value) => prev + value, 0) === 100
              )}
          />
          <br />
          {values.percentageIncomePerState.map((val, index) => (
            <div
              key={`${val.state}-${index}`}
              className="w-full flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-2"
            >
              <div className="sm:w-5/12 w-full">
                <Form.Select
                  name={`percentageIncomePerState[${index}].state`}
                  label="State your clients live in"
                  required
                  options={STATES_LONG.filter(
                    (state) =>
                      isValidUSState(state.value) &&
                      (state.value === val.state ||
                        !values.percentageIncomePerState
                          .map((v) => v.state)
                          .includes(state.value))
                  )}
                  schema={makeReqStringSchema()}
                />
              </div>
              <div className="sm:w-6/12 w-full flex flex-row justify-center items-center">
                <Form.Input
                  name={`percentageIncomePerState[${index}].value`}
                  label="% of income made"
                  required
                  placeholder="Enter %"
                  iconRight={faPercent}
                  step="1"
                  type="number"
                  schema={makeNumberSchema({ maxNumber: 100 })}
                />
                {values.percentageIncomePerState.length > 1 && (
                  <Button
                    className="pt-5"
                    variant="discreet"
                    iconLeft={faTrashCan}
                    onClick={() => handleRemove(index)}
                    aria-label={`delete-state-${index}`}
                  />
                )}
              </div>
            </div>
          ))}

          <Button
            variant="link"
            onClick={() => {
              setFieldValue('percentageIncomePerState', [
                ...values.percentageIncomePerState,
                { state: undefined, value: undefined },
              ]).then(() => validateForm())
            }}
          >
            + Add another state
          </Button>
        </Card>

        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={() => navigate('/taxes/quarterly')}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
